// Plain-English rendering of a commercial deal. Used by the deal cards, the
// live preview line in the editor, and the campaign Commercials card, so the
// operator always confirms the same sentence everywhere:
//
//   "Advertiser pays us $250 + 5% of deposits per qualified FTD
//    (min deposit $20) · capped at 30/month · test phase: first 10"

import type { CapPeriod, DealActionType, DealStatus, DealType, DealSide } from "@/types/cpa";
import { formatBpsPercent, formatUsdShort } from "@/lib/money";

export const ACTION_TYPE_LABEL: Record<DealActionType, string> = {
    click: "Click",
    lead: "Lead",
    ftd: "First-time deposit (FTD)",
};

export const DEAL_TYPE_LABEL: Record<DealType, string> = {
    cpa: "Flat amount per action",
    revshare: "Revenue share",
    hybrid: "Flat amount + revenue share",
};

export const DEAL_STATUS_VARIANT: Record<DealStatus, "success" | "warning" | "muted" | "outline"> = {
    draft: "outline",
    active: "success",
    paused: "warning",
    ended: "muted",
};

/** The fields the summary sentence needs — a DealDto satisfies this, and so does editor form state. */
export interface DealSummaryInput {
    side: DealSide;
    actionType: DealActionType;
    dealType: DealType;
    cpaUsdMicros: number | null;
    revshareBps: number | null;
    qualifyingDepositUsdMicros: number | null;
    capActions: number | null;
    capPeriod: CapPeriod | null;
    testCapActions: number | null;
    testConfirmedAt?: string | null;
}

function actionNoun(input: DealSummaryInput): string {
    switch (input.actionType) {
        case "click":
            return "click";
        case "lead":
            return "lead";
        case "ftd":
            return input.qualifyingDepositUsdMicros != null
                ? `qualified FTD (min deposit ${formatUsdShort(input.qualifyingDepositUsdMicros)})`
                : "FTD";
    }
}

function moneyPhrase(input: DealSummaryInput): string {
    const share = input.actionType === "ftd" ? "of deposits" : "of revenue";
    switch (input.dealType) {
        case "cpa":
            return formatUsdShort(input.cpaUsdMicros);
        case "revshare":
            return `${formatBpsPercent(input.revshareBps)} ${share}`;
        case "hybrid":
            return `${formatUsdShort(input.cpaUsdMicros)} + ${formatBpsPercent(input.revshareBps)} ${share}`;
    }
}

/**
 * The one-line plain-English deal sentence. Advertiser side is money IN
 * ("Advertiser pays us…"), affiliate side is money OUT ("We pay…").
 */
export function dealSummary(input: DealSummaryInput): string {
    const prefix = input.side === "advertiser" ? "Advertiser pays us" : "We pay";
    let text = `${prefix} ${moneyPhrase(input)} per ${actionNoun(input)}`;
    if (input.capActions != null && input.capActions > 0) {
        const per = input.capPeriod === "total" || input.capPeriod == null
            ? `${input.capActions.toLocaleString("en-US")} total`
            : `${input.capActions.toLocaleString("en-US")}/${input.capPeriod}`;
        text += ` · capped at ${per}`;
    }
    if (input.testCapActions != null && input.testCapActions > 0 && input.testConfirmedAt == null) {
        text += ` · test phase: first ${input.testCapActions.toLocaleString("en-US")}`;
    }
    return text;
}
